import { useEffect, useRef, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play,
  Pause,
  RotateCcw,
  Volume2,
  VolumeX,
  Wrench,
  X,
  MessageSquare,
  CalendarCheck,
  CheckCircle2,
  Car,
  DollarSign,
  Share2,
} from "lucide-react";
import { toast } from "sonner";
import DemoQRCode from "@/components/DemoQRCode";

const buildScenes = (s) => {
  const d1 = s?.stage_1_days ?? 7;
  const p1 = s?.stage_1_discount ?? 30;
  return [
    {
      key: "decline",
      caption: "$605 walks out your drive. Every single day.",
      duration: 5200,
    },
    {
      key: "text",
      caption: `One declined RO: $605. How many walk out your drive every day?`,
      duration: 5200,
    },
    {
      key: "tap",
      caption: "Two taps. She books herself.",
      duration: 5000,
    },
    {
      key: "booked",
      caption: "Your service board fills — while your advisors keep advising.",
      duration: 4500,
    },
    {
      key: "return",
      caption: "Wednesday, 1 PM. She rolls back in.",
      duration: 4500,
    },
    {
      key: "recovered",
      caption: "Recovered. Every dollar tracked to a text.",
      duration: 5800,
    },
  ];
};
const SCENES = buildScenes(null);

function DeclineScene() {
  return (
    <div className="w-full h-full flex items-center justify-center px-6">
      <div className="max-w-lg w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl p-5 shadow-2xl text-neutral-900"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-navy grid place-items-center">
              <Wrench className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <div>
              <div className="font-mono-label text-neutral-500 text-[0.6rem]">Recommended service</div>
              <div className="font-display font-bold text-sm">Alicia B. · 2021 Honda CR-V</div>
            </div>
          </div>
          <div className="space-y-1.5 text-sm">
            {[
              { name: "Front brake pads & rotor", price: 342.5 },
              { name: "Cabin air filter", price: 74.0 },
              { name: "Transmission fluid flush", price: 189.0 },
            ].map((s, i) => (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.15 }}
                className="flex justify-between border-b border-neutral-100 py-1"
              >
                <span className="text-neutral-700">{s.name}</span>
                <span className="tabular-nums text-neutral-500">${s.price.toFixed(2)}</span>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{ scale: 0, rotate: -25, opacity: 0 }}
            animate={{ scale: 1, rotate: -12, opacity: 1 }}
            transition={{ delay: 1.6, type: "spring", stiffness: 220, damping: 12 }}
            className="absolute mt-4 ml-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-red-600 text-white text-base font-black tracking-wider shadow-2xl border-2 border-red-800"
            style={{ boxShadow: "0 10px 30px rgba(220,38,38,0.55)" }}
          >
            <X className="w-5 h-5" strokeWidth={3.5} />
            NOT TODAY
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.4, duration: 0.6 }}
          className="text-center mt-16"
        >
          <div
            className="font-display font-black text-3xl md:text-4xl tracking-tighter tabular-nums"
            style={{
              color: "#FFD25F",
              textShadow: "0 0 30px rgba(255,210,95,0.5), 0 2px 4px rgba(0,0,0,0.6)",
            }}
          >
            $605.50
          </div>
          <div className="font-mono-label text-white/70 text-xs md:text-sm mt-1 tracking-widest">
            walks out the drive
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function TextScene({ discount = 30 }) {
  return (
    <div className="w-full h-full flex items-center justify-center px-6">
      <div className="relative">
        {/* Phone frame */}
        <div className="rounded-[2.75rem] border-[11px] border-neutral-800 bg-neutral-900 shadow-2xl w-[320px] h-[600px] overflow-hidden" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.6)" }}>
          <div className="bg-neutral-800 text-white text-[0.65rem] px-4 py-2 flex justify-between items-center">
            <span>9:04 AM</span>
            <span>•••</span>
          </div>
          <div className="bg-neutral-950 h-full pt-10 relative">
            {/* Notification banner slides down */}
            <motion.div
              initial={{ y: -80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, type: "spring", damping: 15 }}
              className="absolute top-10 inset-x-3 rounded-xl bg-neutral-800/95 backdrop-blur-md border border-white/10 p-3.5 shadow-2xl"
              style={{ boxShadow: "0 15px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08) inset" }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-md bg-emerald-600 grid place-items-center">
                  <MessageSquare className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                </div>
                <div className="font-bold text-white text-[0.75rem]">Messages</div>
                <div className="ml-auto text-[0.6rem] text-white/50">now</div>
              </div>
              <div className="text-[0.7rem] font-semibold text-white/95">
                Precision Auto Group
              </div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.3, duration: 0.4 }}
                className="text-[0.7rem] text-white/80 mt-1 leading-snug"
              >
                Hi Alicia, get{" "}
                <span className="text-[hsl(var(--gold-light))] font-bold">
                  {discount}% OFF
                </span>{" "}
                the Front brake pads & rotor…
              </motion.div>
            </motion.div>
            {/* Vibration lines */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0, 1, 0] }}
              transition={{ delay: 0.3, duration: 1 }}
              className="absolute -left-4 top-24 flex flex-col gap-1"
            >
              {[1, 2, 3].map((n) => (
                <div
                  key={n}
                  className="h-0.5 w-3 bg-[hsl(var(--gold-light))] rounded-full"
                />
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TapScene({ discount = 30 }) {
  return (
    <div className="w-full h-full flex items-center justify-center px-6">
      <div className="rounded-[2.75rem] border-[11px] border-neutral-800 bg-neutral-900 shadow-2xl w-[320px] overflow-hidden" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.6)" }}>
        <div className="bg-neutral-800 text-white text-[0.65rem] px-4 py-2 flex justify-between items-center">
          <span>9:05 AM</span>
          <span>•••</span>
        </div>
        <div className="bg-neutral-950 text-white p-4 min-h-[520px]">
          <div className="font-mono-label text-white/60 text-[0.55rem]">
            Pick a day
          </div>
          {/* Fake mini-calendar with cascading time slot fills */}
          <div className="grid grid-cols-7 gap-1 mt-2">
            {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
              <div
                key={d}
                className="text-center text-[0.55rem] text-white/40 font-mono py-1"
              >
                {d}
              </div>
            ))}
            {Array.from({ length: 21 }).map((_, i) => {
              const day = i + 1;
              const isPick = day === 12;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.05 * i }}
                  className={`text-center text-[0.65rem] py-1.5 rounded ${
                    isPick
                      ? "bg-[hsl(var(--gold))] text-neutral-900 font-black"
                      : "text-white/70"
                  }`}
                >
                  {day}
                </motion.div>
              );
            })}
          </div>
          <div className="font-mono-label text-white/60 text-[0.55rem] mt-4">
            Time slot
          </div>
          <div className="grid grid-cols-3 gap-1.5 mt-2">
            {["8:00 AM", "9:30 AM", "11:00 AM", "1:00 PM", "2:30 PM", "4:00 PM"].map((t, i) => {
              const isPick = t === "1:00 PM";
              return (
                <motion.div
                  key={t}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.5 + i * 0.12 }}
                  className={`text-[0.55rem] py-1.5 text-center rounded border ${
                    isPick
                      ? "bg-[hsl(var(--gold))] border-[hsl(var(--gold))] text-neutral-900 font-bold"
                      : "border-white/10 text-white/60"
                  }`}
                >
                  {t}
                </motion.div>
              );
            })}
          </div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3, duration: 0.4 }}
            className="mt-4 rounded-md bg-[hsl(var(--gold))] text-neutral-900 text-center py-2.5 font-bold text-xs"
          >
            Confirm — {discount}% OFF
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function BookedScene() {
  return (
    <div className="w-full h-full flex items-center justify-center px-6">
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", damping: 15 }}
        className="max-w-sm w-full bg-white rounded-xl p-8 text-center shadow-2xl text-neutral-900"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
          className="w-20 h-20 mx-auto rounded-full bg-emerald-500/15 grid place-items-center"
        >
          <CheckCircle2
            className="w-12 h-12 text-emerald-600"
            strokeWidth={2}
          />
        </motion.div>
        <div className="font-display font-black text-2xl mt-4 tracking-tight">
          You&apos;re booked
        </div>
        <div className="font-mono-label text-neutral-500 mt-2">
          Wed · Mar 12 · 1:00 PM
        </div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mt-4 pt-4 border-t border-neutral-200"
        >
          <div className="font-mono-label text-neutral-500">Estimated total</div>
          <div className="font-display text-3xl font-black tabular-nums text-neutral-900 mt-1">
            $423.85
          </div>
          <div className="text-xs text-neutral-400 line-through tabular-nums">
            $605.50
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

function ReturnScene() {
  return (
    <div className="w-full h-full flex items-center justify-center px-6">
      <div className="text-center">
        <motion.div
          initial={{ x: -150, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 1.2, type: "spring", stiffness: 60 }}
          className="inline-block"
        >
          <div className="w-40 h-40 rounded-full bg-[hsl(var(--gold))]/15 grid place-items-center border-4 border-[hsl(var(--gold))]/40">
            <Car
              className="w-24 h-24 text-[hsl(var(--gold-light))]"
              strokeWidth={1.5}
            />
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="mt-6"
        >
          <div className="font-mono-label text-[hsl(var(--gold-light))]">
            Wed · 12:52 PM
          </div>
          <div className="font-display text-2xl font-black text-white mt-2 tracking-tight">
            Alicia rolls into your service bay
          </div>
          <div className="text-sm text-white/60 mt-1">
            Coupon <span className="font-mono text-[hsl(var(--gold-light))]">DD-30-A7B2K9</span> scanned at the desk
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function CountUp({ from = 0, to, prefix = "$", duration = 2 }) {
  const [val, setVal] = useState(from);
  useEffect(() => {
    const start = performance.now();
    let raf;
    const step = (t) => {
      const elapsed = (t - start) / 1000;
      const p = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [from, to, duration]);
  return (
    <span className="tabular-nums">
      {prefix}
      {val.toLocaleString()}
    </span>
  );
}

function RecoveredScene() {
  return (
    <div className="w-full h-full flex items-center justify-center px-6">
      <div className="text-center max-w-lg">
        <motion.div
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", damping: 12, stiffness: 180 }}
          className="w-20 h-20 mx-auto rounded-lg bg-[hsl(var(--gold))] grid place-items-center shadow-2xl"
          style={{ boxShadow: "0 20px 50px rgba(201,162,39,0.55)" }}
        >
          <DollarSign className="w-10 h-10 text-neutral-900" strokeWidth={2.75} />
        </motion.div>
        <div className="font-mono-label text-[hsl(var(--gold-light))] mt-6 tracking-widest">
          Recovered on this RO
        </div>
        <div
          className="font-display font-black text-7xl md:text-8xl mt-3 tracking-tighter"
          style={{
            color: "#FFD25F",
            textShadow:
              "0 0 40px rgba(255,210,95,0.6), 0 0 80px rgba(255,210,95,0.35), 0 4px 8px rgba(0,0,0,0.5)",
          }}
        >
          <CountUp to={423} duration={2.2} />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.5, duration: 0.6 }}
          className="mt-8 grid grid-cols-3 gap-3 text-center"
        >
          <div>
            <div className="font-mono-label text-white/50 text-[0.6rem] tracking-widest">
              Advisor clicks
            </div>
            <div className="font-display font-black text-3xl md:text-4xl text-white tabular-nums mt-1">
              0
            </div>
          </div>
          <div>
            <div className="font-mono-label text-white/50 text-[0.6rem] tracking-widest">
              Time invested
            </div>
            <div className="font-display font-black text-3xl md:text-4xl text-white tabular-nums mt-1">
              0s
            </div>
          </div>
          <div>
            <div className="font-mono-label text-white/50 text-[0.6rem] tracking-widest">
              This month
            </div>
            <div className="font-display font-black text-3xl md:text-4xl text-[hsl(var(--gold))] tabular-nums mt-1">
              ×15
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export const SCENE_COMPONENTS = {
  decline: DeclineScene,
  text: TextScene,
  tap: TapScene,
  booked: BookedScene,
  return: ReturnScene,
  recovered: RecoveredScene,
};
export const DEMO_SCENES = SCENES;

export default function HowItWorksPage() {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [settings, setSettings] = useState(null);
  const audioCtxRef = useRef(null);
  const kachingRef = useRef(null);
  const scenesDyn = buildScenes(settings);
  const current = scenesDyn[idx];

  useEffect(() => {
    import("@/lib/api").then((m) => {
      m.default.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    });
  }, []);

  // HTML5 Audio fallback — plays a real WAV file. Way more reliable than
  // Web Audio on iOS Safari, ringer/silent switch aside.
  useEffect(() => {
    try {
      const a = new Audio("/kaching.wav");
      a.preload = "auto";
      a.volume = 1.0;
      kachingRef.current = a;
    } catch (_) {}
  }, []);

  const playKachingFile = () => {
    const a = kachingRef.current;
    if (!a) return false;
    try {
      a.pause();
      a.currentTime = 0;
      const p = a.play();
      if (p && p.catch) p.catch(() => {});
      return true;
    } catch (_) {
      return false;
    }
  };

  // ---- Audio helpers (all Web Audio, no MP3s) ----
  // Browsers block AudioContext until a user gesture. We create + resume it
  // inside button click handlers (ensureAudio), and only THEN allow the
  // per-scene cue effect to play sounds.
  const ensureAudio = () => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") {
        // resume() returns a Promise — safe to fire-and-forget from a gesture
        ctx.resume().catch(() => {});
      }
      setAudioReady(true);
      return ctx;
    } catch (_) {
      return null;
    }
  };

  const playCashRegister = (ctx, start) => {
    // Drawer clunk (mechanical low thump)
    const clunk = ctx.createOscillator();
    const clunkG = ctx.createGain();
    clunk.type = "sawtooth";
    clunk.frequency.setValueAtTime(90, start);
    clunk.frequency.exponentialRampToValueAtTime(55, start + 0.09);
    clunkG.gain.setValueAtTime(0.0001, start);
    clunkG.gain.exponentialRampToValueAtTime(0.35, start + 0.005);
    clunkG.gain.exponentialRampToValueAtTime(0.0001, start + 0.11);
    clunk.connect(clunkG).connect(ctx.destination);
    clunk.start(start);
    clunk.stop(start + 0.13);
    // Twin-harmonic bell (the "ding")
    const bellStart = start + 0.04;
    [[2500, 0.32], [3800, 0.2]].forEach(([freq, amp]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(freq, bellStart);
      g.gain.setValueAtTime(0.0001, bellStart);
      g.gain.exponentialRampToValueAtTime(amp, bellStart + 0.003);
      g.gain.exponentialRampToValueAtTime(0.0001, bellStart + 0.6);
      o.connect(g).connect(ctx.destination);
      o.start(bellStart);
      o.stop(bellStart + 0.65);
    });
    // Coin clink (bright sparkle)
    const coinStart = start + 0.15;
    const coin = ctx.createOscillator();
    const coinG = ctx.createGain();
    coin.type = "triangle";
    coin.frequency.setValueAtTime(5200, coinStart);
    coin.frequency.exponentialRampToValueAtTime(6800, coinStart + 0.05);
    coinG.gain.setValueAtTime(0.0001, coinStart);
    coinG.gain.exponentialRampToValueAtTime(0.18, coinStart + 0.005);
    coinG.gain.exponentialRampToValueAtTime(0.0001, coinStart + 0.11);
    coin.connect(coinG).connect(ctx.destination);
    coin.start(coinStart);
    coin.stop(coinStart + 0.13);
  };

  const playSceneCue = (ctx, sceneKey) => {
    const now = ctx.currentTime;
    // Scene-change chirps (subtle so they layer under any ka-ching)
    const cues = {
      decline: [[220, 0.35, "sawtooth", 0]],
      text: [[880, 0.1, "sine", 0], [1320, 0.16, "sine", 0.09]],
      tap: [[660, 0.1, "square", 0]],
      booked: [[523, 0.14, "sine", 0], [659, 0.14, "sine", 0.14], [784, 0.28, "sine", 0.28]],
      return: [[400, 0.6, "sine", 0]],
      recovered: [],
    };
    (cues[sceneKey] || []).forEach(([freq, dur, wave, delay]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = wave;
      o.frequency.setValueAtTime(freq, now + delay);
      g.gain.setValueAtTime(0.0001, now + delay);
      g.gain.exponentialRampToValueAtTime(0.25, now + delay + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, now + delay + dur);
      o.connect(g).connect(ctx.destination);
      o.start(now + delay);
      o.stop(now + delay + dur + 0.02);
    });
    // Ka-ching on the two money moments — fire BOTH the Web Audio synth AND
    // the WAV file, whichever the device can actually play will be heard.
    if (sceneKey === "booked") {
      playCashRegister(ctx, now + 0.55);
      setTimeout(() => playKachingFile(), 550);
    }
    if (sceneKey === "recovered") {
      playCashRegister(ctx, now + 0.1);
      setTimeout(() => playKachingFile(), 100);
    }
  };

  // Scene-change effect — only plays audio after the user has enabled it
  useEffect(() => {
    if (muted || !audioReady) return;
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    playSceneCue(ctx, current.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, muted, audioReady, current.key]);

  useEffect(() => {
    if (!playing) return;
    const t = setTimeout(() => {
      setIdx((i) => (i + 1) % SCENES.length);
    }, current.duration);
    return () => clearTimeout(t);
  }, [idx, playing, current.duration]);

  const Scene = SCENE_COMPONENTS[current.key];
  const stage1Pct = settings?.stage_1_discount ?? 30;

  return (
    <DashboardShell>
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Sales Demo</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">
            One declined RO: $605.
            <br />
            How many walk out your drive every day?
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            A 30-second animated walk-through you can show any dealer principal on the iPad.
            Auto-plays, loops, and speaks for itself — no talking head required.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={async () => {
              const url = `${window.location.origin}/demo`;
              try {
                await navigator.clipboard.writeText(url);
                toast.success("Public demo link copied — text or email it to any dealer");
              } catch (_) {
                toast.error(`Copy failed — link is ${url}`);
              }
            }}
            data-testid="how-share-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-md"
          >
            <Share2 className="w-4 h-4 mr-2" strokeWidth={2} />
            Copy Share Link
          </Button>
          <Button
            onClick={() => {
              const willMute = !muted;
              setMuted(willMute);
              if (!willMute) {
                const ctx = ensureAudio();
                if (ctx) playCashRegister(ctx, ctx.currentTime + 0.05);
                playKachingFile();
              }
            }}
            data-testid="how-mute-toggle"
            title={muted ? "Unmute — hear the money" : "Mute scene sounds"}
            className="bg-navy hover:bg-navy/90 text-white border-2 border-[hsl(var(--gold))] shadow-md"
          >
            {muted ? <VolumeX className="w-4 h-4" strokeWidth={2} /> : <Volume2 className="w-4 h-4" strokeWidth={2} />}
          </Button>
          <Button
            onClick={() => {
              ensureAudio();
              setPlaying((p) => !p);
            }}
            data-testid="how-play-toggle"
            className="bg-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/90 text-neutral-900 font-bold shadow-md"
          >
            {playing ? (
              <>
                <Pause className="w-4 h-4 mr-2" strokeWidth={2.5} />
                Pause
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" strokeWidth={2.5} />
                Play
              </>
            )}
          </Button>
          <Button
            onClick={() => {
              ensureAudio();
              setIdx(0);
              setPlaying(true);
            }}
            data-testid="how-replay"
            className="bg-navy hover:bg-navy/90 text-white border-2 border-[hsl(var(--gold))] shadow-md"
          >
            <RotateCcw className="w-4 h-4 mr-2" strokeWidth={2} />
            Replay
          </Button>
        </div>
      </div>

      {/* Stage */}
      <Card
        className="relative overflow-hidden border-[hsl(var(--gold))]/30 shadow-xl"
        data-testid="how-stage"
      >
        <div className="relative bg-gradient-to-br from-navy via-[#0a1d4a] to-[#050f2b] text-white h-[600px] md:h-[720px]">
          {/* Cinematic vignette — darker corners keep eyes on the center */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.55) 100%)",
            }}
          />
          {/* TOP caption — dark pill backdrop keeps text readable over any scene */}
          <div className="absolute inset-x-0 top-0 z-10 px-5 pt-5 pb-3 pointer-events-none">
            <AnimatePresence mode="wait">
              <motion.div
                key={`cap-top-${current.key}`}
                initial={{ opacity: 0, y: -14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.4 }}
                className="flex justify-center"
                data-testid="how-caption-top"
              >
                <div
                  className="inline-block px-7 py-3.5 rounded-full bg-neutral-950/90 border border-[hsl(var(--gold))]/50 shadow-2xl max-w-3xl"
                  style={{ backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
                >
                  <div
                    className="font-display font-black text-xl md:text-3xl tracking-tighter text-center leading-tight"
                    style={{
                      color: "#FFD25F",
                      textShadow: "0 0 20px rgba(255,210,95,0.4), 0 2px 4px rgba(0,0,0,0.5)",
                    }}
                  >
                    {current.caption}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Very subtle noise / grid overlay — barely visible, adds texture */}
          <div
            className="absolute inset-0 opacity-[0.03] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />

          <AnimatePresence mode="wait">
            <motion.div
              key={current.key}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="absolute inset-0"
            >
              <Scene discount={stage1Pct} />
            </motion.div>
          </AnimatePresence>

          {/* Bottom — progress dots only */}
          <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/70 to-transparent">
            <div className="flex gap-1.5">
              {SCENES.map((s, i) => (
                <button
                  key={s.key}
                  onClick={() => {
                    ensureAudio();
                    setIdx(i);
                    setPlaying(true);
                  }}
                  data-testid={`how-dot-${s.key}`}
                  className="h-1 flex-1 rounded-full bg-white/15 overflow-hidden transition-all hover:bg-white/25"
                  aria-label={`Jump to scene ${i + 1}`}
                >
                  {i === idx && playing && (
                    <motion.div
                      key={`bar-${idx}`}
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: current.duration / 1000, ease: "linear" }}
                      className="h-full bg-[hsl(var(--gold))]"
                    />
                  )}
                  {i < idx && <div className="h-full w-full bg-[hsl(var(--gold))]/60" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Big obvious TEST SOUND button — fires both audio paths so at least
          one plays. If neither works, iPad silent switch is on. */}
      <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-4 no-print" data-testid="sound-test-row">
        <Button
          onClick={() => {
            const ctx = ensureAudio();
            if (ctx) playCashRegister(ctx, ctx.currentTime + 0.05);
            playKachingFile();
          }}
          data-testid="sound-test-btn"
          className="bg-red-600 hover:bg-red-700 text-white font-black text-base h-12 px-6 shadow-lg animate-pulse"
        >
          <Volume2 className="w-5 h-5 mr-2" strokeWidth={2.5} />
          🔊 TEST SOUND — Tap to hear the ka-ching
        </Button>
        <DemoQRCode size={110} caption="Or scan · dealer gets the demo on their phone" />
      </div>

      {/* Post-demo pitch */}
      <div className="mt-6 grid md:grid-cols-3 gap-4">
        {[
          {
            Icon: CalendarCheck,
            title: "Zero clicks from your team",
            body:
              "Nightly DMS pull → automatic text → customer books themselves. Advisors keep advising.",
          },
          {
            Icon: DollarSign,
            title: "Every recovered RO tracked",
            body:
              "Every text has a unique coupon. When it hits the service desk, the recovered dollars land on your KPI card in real time.",
          },
          {
            Icon: CheckCircle2,
            title: "Live in under a week",
            body:
              "One data-consent form on your side. We do everything else. Text #1 fires by day 2.",
          },
        ].map(({ Icon, title, body }) => (
          <Card key={title} className="p-5 border-border">
            <div className="w-9 h-9 rounded-md bg-navy text-white grid place-items-center">
              <Icon className="w-4 h-4" strokeWidth={2} />
            </div>
            <div className="font-display font-bold mt-3">{title}</div>
            <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {body}
            </div>
          </Card>
        ))}
      </div>

      <div className="mt-6 text-xs text-muted-foreground border-t border-border pt-4 max-w-3xl">
        Tip: On the iPad, hand the tablet to the dealer principal during scene 2 —
        they intuitively watch the phone-notification animation and it sells itself.
      </div>
    </DashboardShell>
  );
}
